<template>
  <div>
    <b-card title="Sorting Session Report">
      <b-row>
        <b-col
          cols="12"
          lg="4"
          class="align-items-start"
        >
          <b-form-group label="Sorting Date">
            <b-form-datepicker
              v-model="sortingDate"
              class="w-100 sorting-date"
              @input="getSortingIdString"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          lg="4"
          class="align-items-start"
        >
          <b-form-group label="Selected Bucket">
            <v-select
              v-model="bucket"
              :reduce="option => option.value"
              label="name"
              placeholder="Select Bucket"
              :options="availableBuckets"
              @input="getSortingIdString"
            >
              <template v-slot:option="option">
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          md="3"
          class="mt-md-2"
        >
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            @click="getSessionStats()"
          >
            <feather-icon
              icon="CornerLeftDownIcon"
              class="mr-50"
            />
            <span class="align-middle">Get Data</span>
          </b-button>
        </b-col>
      </b-row>
    </b-card>
    <b-row v-if="bucketName === 'Master-Bucket' && loading === false">
      <b-col
        v-if="actualRate"
        md="6"
      >
        <scan-performance-card
          title="Current Hourly Scan Rate (Users totals Combined)"
          icon="TrendingUpIcon"
          indicator-color="#A52A2A"
          :tile-value="actualRate + ' Scans/hour'"
        />
      </b-col>
      <b-col
        v-if="requiredRate"
        md="6"
      >
        <scan-performance-card
          title="Required Hourly Scan Rate to end at 2.00 AM (Users totals Combined)"
          icon="TargetIcon"
          indicator-color="#9FC87E"
          :tile-value="requiredRate + ' Scans/hour'"
        />
      </b-col>
      <b-col
        v-if="sessionstartedAt"
        md="6"
      >
        <scan-performance-card
          title="Current Sorting Session Started At"
          icon="PlayCircleIcon"
          indicator-color="#0000FF"
          :tile-value="sessionstartedAt"
        />
      </b-col>
      <b-col
        v-if="estimatedEndTime"
        md="6"
      >
        <scan-performance-card
          title="Estimated Sorting End Time with Current Scan Rate"
          icon="ClockIcon"
          indicator-color="#E9A319"
          :tile-value="estimatedEndTime"
        />
      </b-col>
    </b-row>
    <div v-if="sessions && sessions.length">
      <!-- pass the same `sessions` you fetched -->
      <HourlyScansChart :sessions="sessions" />
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BFormGroup, BFormDatepicker,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import SuccessBell from '@core/mixins/SuccessBell'
import ErrorBell from '@core/mixins/ErrorBell'
import HourlyScansChart from '@/components/Sorting/HourlyScansChart.vue'
import ScanPerformanceCard from '@/@core/components/sorting-scan-performance-card/ScanPerformanceCard.vue'

const sortingRepository = RepositoryFactory.get('sorting')

export default {
  directives: {
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BFormGroup,
    BFormDatepicker,
    HourlyScansChart,
    ScanPerformanceCard,
  },
  mixins: [SuccessMessage, ErrorMessage, SuccessBell, ErrorBell],
  data: () => ({
    loading: false, // Add loading state
    loggedIn: false,
    upcoming: 0,
    toSort: 0,
    sorted: 0,
    availableBuckets: [],
    sortingDate: new Date().toISOString().split('T')[0],
    sortingId: '',
    category: 'Night Operations',
    bucket: null,
    currentLocation: '',
    fullPath: [],
    buckets: [],
    bucketName: '',
    scanStatus: '',
    scanMsg: '',
    scanCode: '',
    scanBucket: '',
    scanWaybill: '',
    isStarting: false,
    hideWidgets: true,
    bucketColor: 'rgb(115, 103, 240)', // default color
    sortingIdString: '',
    sessions: [],
    requiredRate: 0,
    estimatedEndTime: '',
  }),
  mounted() {
    this.fetchBuckets()
  },
  methods: {
    async getSessionStats() {
      if (!this.bucket) {
        this.showErrorMessage('Please select a bucket')
        return
      }
      try {
        const response = await sortingRepository.getSessionStats(this.sortingIdString)
        this.sessions = response.data.data.sessions || []
        this.getScanPerformance()
      } catch (error) {
        // console.error('Error fetching available buckets', error)
      }
    },
    async getScanPerformance() {
      try {
        this.loading = true
        const { data } = await sortingRepository.getScanPerformance(this.sortingIdString)
        this.requiredRate = data.data ? data.data.required_rate : 0
        this.actualRate = data.data ? data.data.actual_rate : 0
        this.sessionstartedAt = data.data ? data.data.session_start : ''
        this.estimatedEndTime = data.data ? data.data.estimated_end_time : ''
      } catch (error) {
        // console.error('Error fetching available buckets', error)
      } finally {
        this.loading = false
      }
    },
    async getSortingIdString() {
      if (this.bucket && this.sortingDate) {
        const selectedBucketObj = this.availableBuckets.find(
          bucket => bucket.value === this.bucket,
        )
        const bucketName = selectedBucketObj ? selectedBucketObj.name.replace(/\s+/g, '-') : ''
        this.sortingIdString = `${this.sortingDate}-Night-Ops-${bucketName}`
        this.bucketName = bucketName
      } else {
        this.sortingIdString = ''
      }
    },
    async fetchBuckets() {
      try {
        const response = await sortingRepository.getAvailableBucketsforReporting()
        this.availableBuckets = (response.data.data ?? []).map(bucket => ({
          value: bucket.id.toString(),
          text: `${bucket.name} (${bucket.key})`,
          key: bucket.key,
          name: bucket.name,
        }))
      } catch (error) {
        // console.error('Error fetching available buckets', error)
      }
    },
  },
}
</script>
<style scoped>
.close-bucket {
  background-color: #fff !important;
  border: #fff !important;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  color: rgb(186, 0, 0) !important;
  font-weight: 500 !important;
}
.dispatch-button {
  background-color: #fff !important;
  border: #fff !important;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  color: rgb(28, 0, 152) !important;
  font-weight: 500 !important;
}
</style>
